import { Input as HeroInput, InputProps as HeroInputProps } from '@heroui/react'
import { AsYouType } from 'libphonenumber-js'
import { useEffect, useState } from 'react'

export interface PhoneInputProps extends Omit<HeroInputProps, 'onChange'> {
  label?: string
  placeholder?: string
  variant?: 'bordered' | 'flat' | 'faded' | 'underlined'
  labelPlacement?: 'outside' | 'inside' | 'outside-left'
  name?: string
  isRequired?: boolean
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void // emits normalized E.164 (+[digits])
  onBlur?: () => void
  errorMessage?: string
  classNames?: {
    label?: string[]
    input?: string[]
    innerWrapper?: string
    inputWrapper?: string[]
  }
}

const ONLY_ALLOWED = /^[\d\s()+-]*$/

const toE164 = (raw: string) => {
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '')

  if (!digits) return ''

  // Always normalize to leading '+'
  return `+${digits}`
}

// Generic international rule per requirements:
// +, then 1–4 digits (CC), then 7–15 digits (national)  => overall 8–19 digits after '+'
const isValidIntl = (e164: string) => {
  if (!e164 || e164[0] !== '+') return false
  const digits = e164.slice(1)

  // Total digits must be between 8-19 (1-4 for country code + 7-15 for national)
  if (digits.length < 8 || digits.length > 19) return false

  // Validate that we have a reasonable country code (1-4 digits) and national number (7-15 digits)
  // We need to check all possible splits to ensure at least one valid combination exists
  for (let ccLength = 1; ccLength <= 4; ccLength++) {
    const nationalLength = digits.length - ccLength

    if (nationalLength >= 7 && nationalLength <= 15) {
      return true
    }
  }

  return false
}

const formatIntlDisplay = (raw: string) => {
  if (!raw) return ''
  // Keep a single leading '+' if present; AsYouType will space/group nicely
  const hasPlus = raw.trim().startsWith('+')
  const digits = raw.replace(/\D/g, '')
  const input = hasPlus ? `+${digits}` : `+${digits}`

  return new AsYouType().input(input)
}

const PhoneInput = ({
  variant = 'bordered',
  labelPlacement = 'outside',
  name,
  isRequired = false,
  value,
  onChange,
  onBlur,
  errorMessage,
  placeholder = 'Enter phone number (e.g. +44 20 7946 0018)',
  classNames,
  defaultValue,
  ...props
}: PhoneInputProps) => {
  const [displayValue, setDisplayValue] = useState(defaultValue || value || '')

  useEffect(() => {
    if (value !== undefined) {
      // If parent controls value, reflect it formatted
      setDisplayValue(formatIntlDisplay(value))
    }
  }, [value])

  const handleChange = (inputValue: string) => {
    // Allow delete/clear freely
    if (!inputValue) {
      setDisplayValue('')
      onChange?.('')

      return
    }

    // Block disallowed chars early (letters, symbols)
    if (!ONLY_ALLOWED.test(inputValue)) return

    // Limit total digits to 19 (maximum valid international phone number length)
    const digits = inputValue.replace(/\D/g, '')

    if (digits.length > 19) return

    // Format for display
    const formattedDisplay = formatIntlDisplay(inputValue)

    setDisplayValue(formattedDisplay)

    // Emit normalized E.164
    const e164 = toE164(inputValue)

    onChange?.(e164)
  }

  return (
    <HeroInput
      fullWidth
      isRequired={isRequired}
      {...props}
      errorMessage={errorMessage}
      labelPlacement={labelPlacement}
      name={name}
      placeholder={placeholder}
      radius="sm"
      type="tel"
      value={displayValue}
      variant={variant}
      classNames={{
        label: [
          'text-gray-900',
          'dark:text-foreground',
          'group-data-[filled-within=true]:text-gray-900',
          'dark:group-data-[filled-within=true]:text-white',
          'group-data-[invalid=true]:text-danger',
          'dark:group-data-[invalid=true]:text-danger',
          ...(classNames?.label || []),
        ],
        input: [
          'bg-transparent',
          'text-gray-900',
          'dark:text-white',
          'placeholder:text-gray-500',
          'dark:placeholder:text-white/60',
          'group-data-[invalid=true]:placeholder:text-danger',
          ...(classNames?.input || []),
        ],
        innerWrapper: 'bg-transparent',
        inputWrapper: [
          'bg-gray-50',
          'dark:bg-[#1e1e20]',
          'border-gray-200',
          'dark:border-default-600',
          'hover:bg-gray-100',
          'dark:hover:bg-[#1e1e20]/70',
          'group-data-[focus=true]:bg-gray-100',
          'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
          'group-data-[focus=true]:border-primary',
          'dark:group-data-[focus=true]:border-primary',
          '!cursor-text',
          ...(classNames?.inputWrapper || []),
        ],
      }}
      validate={(val: string) => {
        const e164 = toE164(val)

        if (isRequired && !e164) return 'Phone number is required'
        if (e164 && !isValidIntl(e164)) return 'Phone number is invalid'

        return
      }}
      onBlur={onBlur}
      onValueChange={handleChange}
    />
  )
}

export default PhoneInput
